import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sprout, Phone, Mail, Calendar, Users as UsersIcon, MapPin, MessageCircle, IndianRupee, CalendarPlus, CheckCheck, QrCode } from "lucide-react";

// Allowed statuses (per spec — "New" removed). Pipeline:
// request_submitted → trial_scheduled → payment_pending → payment_complete | rejected
const STATUS_LABEL = {
  request_submitted: "Request Submitted",
  trial_scheduled: "Trial Scheduled",
  payment_pending: "Payment Pending",
  payment_complete: "Payment Complete",
  rejected: "Rejected",
};
const STATUS_OPTIONS = ["request_submitted", "trial_scheduled", "payment_pending", "payment_complete", "rejected"];
const STATUS_COLORS = {
  request_submitted: "bg-blue-100 text-blue-800 border-blue-300",
  trial_scheduled: "bg-purple-100 text-purple-800 border-purple-300",
  payment_pending: "bg-orange-100 text-orange-800 border-orange-300",
  payment_complete: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

// Subscription type filter values — Party included
const SUB_TYPES = ["monthly", "one-time", "party", "custom"];

// Normalise legacy statuses ('new', 'payment_done') into the new pipeline labels
const normaliseStatus = (s) => {
  if (s === "new") return "request_submitted";
  if (s === "payment_done") return "payment_complete";
  return s || "request_submitted";
};

const fmt = (s) => s ? new Date(s).toLocaleString() : "";

export default function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // Three global filters: customer name, status, subscription type
  const [fStatus, setFStatus] = useState("all");
  const [fSubType, setFSubType] = useState("all");
  const [fCustomer, setFCustomer] = useState("");

  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  // Schedule trial dialog
  const [trialFor, setTrialFor] = useState(null);
  const [trialNote, setTrialNote] = useState("");

  // Payment dialog state — admin enters amount manually, system generates UPI QR
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDue, setPayDue] = useState("");
  const [payDesc, setPayDesc] = useState("Subscription payment");

  // QR preview dialog (post-send confirmation)
  const [qrPreview, setQrPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      // Translate "request_submitted" → also include legacy "new" rows
      if (fStatus !== "all" && fStatus !== "request_submitted") params.status = fStatus;
      const { data } = await api.get("/admin/leads", { params });
      setLeads(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load leads");
    } finally {
      setLoading(false);
    }
  }, [fStatus]);

  useEffect(() => { load(); }, [load]);

  const visibleLeads = useMemo(() => leads.filter(l => {
    const status = normaliseStatus(l.status);
    if (fStatus !== "all" && status !== fStatus) return false;
    if (fSubType !== "all" && l.service_type !== fSubType) return false;
    if (fCustomer && !(l.customer_name || "").toLowerCase().includes(fCustomer.toLowerCase())) return false;
    return true;
  }), [leads, fStatus, fSubType, fCustomer]);

  // Customer name autocomplete options derived from current leads
  const customerNames = useMemo(() => {
    const seen = new Set();
    return leads
      .map(l => l.customer_name)
      .filter(n => n && !seen.has(n) && seen.add(n));
  }, [leads]);

  const saveNotes = async () => {
    if (!selected) return;
    try {
      await api.patch(`/admin/leads/${selected.id}`, { admin_notes: notes });
      toast.success("Saved");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const openTrialDialog = (lead) => {
    setTrialFor(lead);
    setTrialNote("");
  };

  const pushScheduleTrial = async () => {
    if (!trialFor) return;
    try {
      await api.post(`/admin/leads/${trialFor.id}/schedule-trial`, { note: trialNote });
      toast.success("Trial picker pushed to customer");
      setTrialFor(null); setTrialNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't push trial");
    }
  };

  const sendPaymentLink = async () => {
    if (!payFor) return;
    const amt = Number(payAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const { data } = await api.post(`/admin/leads/${payFor.id}/payment-link`, {
        amount: amt, description: payDesc, due_date: payDue || null,
      });
      toast.success("Payment QR sent to customer");
      setPayFor(null); setPayAmount(""); setPayDue(""); setPayDesc("Subscription payment");
      setQrPreview({ ...data, customer_name: payFor.customer_name });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send"); }
  };

  const markPaymentDone = async (lead) => {
    if (!window.confirm(`Mark payment as received for ${lead.customer_name}?`)) return;
    try {
      await api.post(`/admin/leads/${lead.id}/mark-payment-done`);
      toast.success("Payment marked as received");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };

  const waLink = (lead) => {
    const phone = (lead.customer_phone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(`Hi ${lead.customer_name}, reaching out about your ${lead.service_type} service request. When's a good time to talk?`);
    return phone ? `https://wa.me/${phone}?text=${msg}` : null;
  };

  // Button gating per spec
  const canScheduleTrial = (status) => status === "request_submitted";   // one-shot only
  const canSendPayLink   = (status) => status === "payment_pending";     // only after Subscribe / on renewal
  const canMarkPaid      = (status, lead) => status === "payment_pending" && !!lead.last_payment_id;

  return (
    <div className="card-default space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sprout size={18} className="text-primary"/>
          <h3 className="heading text-xl">Leads</h3>
          <Badge variant="outline" className="rounded-full">{visibleLeads.length}</Badge>
        </div>
      </div>

      {/* Global filters */}
      <div className="grid sm:grid-cols-3 gap-3 p-3 rounded-xl bg-muted/40 border border-border">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Customer name</Label>
          <Input
            list="lead-customer-names"
            value={fCustomer}
            onChange={e=>setFCustomer(e.target.value)}
            placeholder="Search or pick…"
            data-testid="filter-customer"
          />
          <datalist id="lead-customer-names">
            {customerNames.map(n => <option key={n} value={n}/>)}
          </datalist>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</Label>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Subscription type</Label>
          <Select value={fSubType} onValueChange={setFSubType}>
            <SelectTrigger data-testid="filter-subtype"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {SUB_TYPES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
       visibleLeads.length === 0 ? <p className="text-sm text-muted-foreground text-center py-10">No leads match the filters.</p> :
       <div className="space-y-3">
         {visibleLeads.map(l => {
           const wa = waLink(l);
           const status = normaliseStatus(l.status);
           return (
             <div key={l.id} className="p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors" data-testid={`lead-${l.id}`}>
               <div className="flex items-start justify-between gap-3 flex-wrap">
                 <div className="min-w-0 flex-1">
                   <div className="flex items-center gap-2 flex-wrap mb-1">
                     <h4 className="font-semibold text-base">{l.customer_name}</h4>
                     <Badge className={`rounded-full text-[10px] border ${STATUS_COLORS[status] || ""}`} data-testid={`lead-tag-${l.id}`}>{STATUS_LABEL[status] || status}</Badge>
                     <Badge variant="outline" className="rounded-full text-[10px] capitalize">{l.service_type}</Badge>
                   </div>
                   <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                     <span className="inline-flex items-center gap-1"><Phone size={11}/>{l.customer_phone}</span>
                     {l.customer_email && !l.customer_email.endsWith("@phone.delcochefs.local") && (
                       <span className="inline-flex items-center gap-1"><Mail size={11}/>{l.customer_email}</span>
                     )}
                     <span className="inline-flex items-center gap-1"><UsersIcon size={11}/>{l.total_persons} pax · {l.dietary}</span>
                     <span className="inline-flex items-center gap-1"><Calendar size={11}/>{fmt(l.created_at)}</span>
                   </div>
                   <div className="text-xs text-muted-foreground mt-1.5 space-y-0.5">
                     {l.service_type === "monthly" && (
                       <div>📅 {l.start_date} → {l.end_date} · 🍽 {(l.meals || []).join(", ")}</div>
                     )}
                     {(l.service_type === "one-time" || l.service_type === "party") && (
                       <div className="inline-flex items-center gap-1"><MapPin size={11}/> {l.service_date} {l.service_time} · {l.venue}</div>
                     )}
                     {l.service_type === "custom" && (
                       <div>📅 {l.date_range_start} → {l.date_range_end}</div>
                     )}
                     {l.allergies && <div className="text-rose-700">⚠️ Allergies: {l.allergies}</div>}
                     {l.special_instructions && <div className="italic">&ldquo;{l.special_instructions}&rdquo;</div>}
                     {l.trial_pushed_at && !l.trial_confirmed_slot && (
                       <div className="bg-purple-50 border border-purple-200 rounded px-2 py-1 mt-1">
                         <b>Trial picker opened</b> — waiting for customer to pick a date+time.
                       </div>
                     )}
                     {l.trial_confirmed_slot && (
                       <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-1">
                         <b>Trial scheduled:</b> {l.trial_confirmed_slot.date} at {l.trial_confirmed_slot.time}
                       </div>
                     )}
                     {l.renewal_pending && (
                       <div className="bg-orange-50 border border-orange-200 rounded px-2 py-1 mt-1">
                         <b>Renewal due:</b> monthly cycle ended {l.end_date}. Send next payment link.
                       </div>
                     )}
                     {l.admin_notes && <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1"><b>Note:</b> {l.admin_notes}</div>}
                   </div>
                 </div>

                 {/* Action buttons — NO per-lead status dropdown */}
                 <div className="flex gap-1.5 flex-wrap justify-end shrink-0">
                   {wa && (
                     <a href={wa} target="_blank" rel="noreferrer">
                       <Button size="sm" variant="outline" className="rounded-full h-8 text-xs">
                         <MessageCircle size={12} className="mr-1"/>WA
                       </Button>
                     </a>
                   )}
                   <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={()=>{ setSelected(l); setNotes(l.admin_notes || ""); }} data-testid={`lead-note-${l.id}`}>
                     Notes
                   </Button>
                   <Button
                     size="sm"
                     disabled={!canScheduleTrial(status)}
                     className={`rounded-full h-8 text-xs ${canScheduleTrial(status) ? "bg-purple-600 hover:bg-purple-700 text-white" : "bg-muted text-muted-foreground cursor-not-allowed"}`}
                     onClick={()=> canScheduleTrial(status) && openTrialDialog(l)}
                     data-testid={`trial-${l.id}`}
                   >
                     <CalendarPlus size={11} className="mr-1"/>Schedule trial
                   </Button>
                   <Button
                     size="sm"
                     disabled={!canSendPayLink(status)}
                     className={`rounded-full h-8 text-xs ${canSendPayLink(status) ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-muted text-muted-foreground cursor-not-allowed"}`}
                     onClick={()=> canSendPayLink(status) && setPayFor(l)}
                     data-testid={`pay-link-${l.id}`}
                   >
                     <IndianRupee size={11} className="mr-0.5"/>Pay link
                   </Button>
                   {canMarkPaid(status, l) && (
                     <Button size="sm" className="rounded-full h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" onClick={()=>markPaymentDone(l)} data-testid={`pay-done-${l.id}`}>
                       <CheckCheck size={11} className="mr-1"/>Mark paid
                     </Button>
                   )}
                 </div>
               </div>
             </div>
           );
         })}
       </div>
      }

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Admin notes — {selected?.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={5} placeholder="Trial dates discussed, preferences, anything to remember..." data-testid="lead-notes-input"/>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={()=>setSelected(null)}>Cancel</Button>
              <Button className="bg-primary hover:bg-primary/90" onClick={saveNotes} data-testid="lead-notes-save">Save notes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!trialFor} onOpenChange={(o) => { if (!o) setTrialFor(null); }}>
        <DialogContent data-testid="trial-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Schedule trial — {trialFor?.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The customer will get a notification and pick any date+time (<b>6 AM – 9 PM</b>) for their trial.
              This action is one-shot — once pushed, the button is disabled for this lead.
            </p>
            <div>
              <Label className="text-xs">Note for customer (optional)</Label>
              <Textarea value={trialNote} onChange={e=>setTrialNote(e.target.value)} placeholder="Any specific guidance — what to expect at the trial…" data-testid="trial-note" rows={3}/>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={()=>setTrialFor(null)}>Cancel</Button>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={pushScheduleTrial} data-testid="trial-push">Send to customer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={(o) => { if (!o) setPayFor(null); }}>
        <DialogContent data-testid="pay-link-dialog">
          <DialogHeader><DialogTitle>Send payment QR — {payFor?.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (₹) <span className="text-rose-500">*</span></Label>
              <Input type="number" min={1} value={payAmount} onChange={e=>setPayAmount(e.target.value)} placeholder="Enter payable amount" data-testid="pay-amount"/>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={payDesc} onChange={e=>setPayDesc(e.target.value)} data-testid="pay-desc"/>
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={payDue} onChange={e=>setPayDue(e.target.value)} data-testid="pay-due"/>
            </div>
            <div className="text-xs text-[#1d1d1f]/60 bg-amber-50 border border-amber-200 rounded-lg p-2">
              💡 A UPI QR is generated with the amount prepopulated, paid to our company VPA. Customer can scan with any UPI app and confirm receipt manually.
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={()=>setPayFor(null)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={sendPaymentLink} data-testid="pay-send">
                <QrCode size={14} className="mr-1"/>Generate &amp; send QR
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrPreview} onOpenChange={(o) => { if (!o) setQrPreview(null); }}>
        <DialogContent data-testid="qr-preview-dialog">
          <DialogHeader><DialogTitle>QR sent to {qrPreview?.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center">
            {qrPreview?.qr_image_url && (
              <img src={qrPreview.qr_image_url} alt="UPI QR" className="mx-auto rounded-xl border border-border" width={260} height={260}/>
            )}
            <div className="text-2xl heading font-bold inline-flex items-center justify-center">
              <IndianRupee size={20} strokeWidth={2.5}/>{Number(qrPreview?.amount || 0).toLocaleString("en-IN")}
            </div>
            <p className="text-xs text-muted-foreground break-all">{qrPreview?.upi_uri}</p>
            <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={()=>setQrPreview(null)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
